import React, { useState, useRef} from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useNavigate, useLocation } from 'react-router-dom';
import './ArtistDashboard.css';

import ArtistPostForm from './ArtistPostForm';
import ArtistPostList from './ArtistPostList';
import ProfilePicture from './ProfilePicture';
import EditableProfilePicture from './EditableProfilePicture';
import CreateAlbumForm from './CreateAlbumForm';
import CreateSongForm from './CreateSongForm';
import CreateShowForm from './CreateShowForm';
import AlbumSongList from './AlbumSongList';

const GET_ARTIST_ALBUMS = gql`
  query GetAlbumsByArtist($artistPageId: ID!) {
    getAlbumsByArtist(artistPageId: $artistPageId) {
      id
      title
    }
  }
`;

const MY_MERCH_PAGE = gql`
  query MyMerchPage($ownerId: ID!) {
    myMerchPage(ownerId: $ownerId) {
      id
      title
      description
      ownerId
    }
  }
`;

const CREATE_MERCHANDISE_PAGE = gql`
  mutation CreateMerchandisePage($input: MerchandisePageInput!) {
    CreateMerchandisePage(input: $input) {
      id
      title
      description
      ownerId
    }
  }
`;

function ArtistDashboard() {
  const [selectedTab, setSelectedTab] = useState('posts');
  const navigate = useNavigate();
  const { state } = useLocation();
  const title = state?.title;
  const ownerId = state?.artistPageId;
  const profileImage = state?.profileImage;
  const token = sessionStorage.getItem('token');
  const postListRefetch = useRef(null);
  const songListRefetch = useRef(null);


  const { data, loading, error, refetch } = useQuery(MY_MERCH_PAGE, {
    variables: { ownerId },
    context: { headers: { Authorization: `Bearer ${token}` } },
  });

  const [createMerchPage, { loading: creating }] = useMutation(CREATE_MERCHANDISE_PAGE, {
    context: { headers: { Authorization: `Bearer ${token}` } },
    onCompleted: () => refetch(),
  });

  const {
    data: albumData,
    refetch: refetchAlbums,
  } = useQuery(GET_ARTIST_ALBUMS, {
    variables: { artistPageId: ownerId },
    context: { headers: { Authorization: `Bearer ${token}` } },
  });

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error loading dashboard: {error.message}</p>;

  const merchPage = data?.myMerchPage?.[0];

  return (
    <div className="artist-dashboard">
      <EditableProfilePicture
        artistPageId={ownerId}
        imageUrl={profileImage}
        refetch={refetch}
      />
      <h1>{title}</h1>

      <div className="dashboard-tabs">
        <button
          className={selectedTab === 'posts' ? 'active' : ''}
          onClick={() => setSelectedTab('posts')}
        >
          📝 Posts
        </button>
        <button
          className={selectedTab === 'music' ? 'active' : ''}
          onClick={() => setSelectedTab('music')}
        >
          🎵 Music
        </button>
        <button
          className={selectedTab === 'merch' ? 'active' : ''}
          onClick={() => setSelectedTab('merch')}
        >
          🛍 Merch
        </button>
        <button
          className={selectedTab === 'shows' ? 'active' : ''}
          onClick={() => setSelectedTab('shows')}
        >
          🎫 Shows
        </button>
      </div>

      {selectedTab === 'posts' && (
        <div className="post-area">
          <h2>Artist Posts</h2>
          <ArtistPostForm
  artistPageId={ownerId}
  onPostCreated={() => {
    console.log("🚀 postListRefetch.current:", postListRefetch.current);
    if (typeof postListRefetch.current === 'function') {
      postListRefetch.current();
    } else {
      console.warn("❌ postListRefetch is NOT a function");
    }
  }}
/>
<ArtistPostList
  artistPageId={ownerId}
  refetchTrigger={(fn) => (postListRefetch.current = fn)}
/>
        </div>
      )}

      {selectedTab === 'music' && (
        <div className="music-area">
          <h2>Your Music</h2>
          <AlbumSongList artistPageId={ownerId} refetchTrigger={(fn) => (songListRefetch.current = fn)} />
          <CreateAlbumForm artistPageId={ownerId} refetch={refetchAlbums} />
          <h3>Albums:</h3>
          {albumData?.getAlbumsByArtist?.length > 0 ? (
            <ul>
              {albumData.getAlbumsByArtist.map((album) => (
                <li key={album.id}>
                  <strong>{album.title}</strong>
                  <CreateSongForm
                    artistPageId={ownerId}
                    albumId={album.id}
                    refetch={refetchAlbums}
                    refetchSongs={songListRefetch.current}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <p>No albums yet. Create one above.</p>
          )}
          
        </div>
      )}

      {selectedTab === 'merch' && (
        <div className="merch-area">
          <h2>Your Merchandise Page</h2>
          {!merchPage ? (
            <button onClick={() => createMerchPage({
              variables: {
                input: {
                  title: title,
                  description: `${title}'s official merchandise page`,
                  ownerId: ownerId,
                },
              }
            })} disabled={creating}>
              {creating ? "Creating..." : "+ Create Merchandise Page"}
            </button>
          ) : (
            <button onClick={() => navigate(`/merchandisedashboard/${merchPage.id}`)}>
              {merchPage.title}
            </button>
          )}
        </div>
      )}

      {selectedTab === 'shows' && (
        <div className="shows-area">
          <h2>Shows</h2>
          <CreateShowForm artistPageId={ownerId} />
        </div>
      )}
    </div>
  );
}

export default ArtistDashboard;
